import { useContext, useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';
import Topbar from './Topbar';
import { AuthContext } from '../contexts/AuthContext'; // Import the AuthContext
import './Layout.css';

const Layout = ({ children, mainContentClass = "" }) => {
    const { loggedIn } = useContext(AuthContext);
    const navigate = useNavigate();
    const location = useLocation();

    // Add sidebar collapse state management with responsive behavior
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const [showMobileOverlay, setShowMobileOverlay] = useState(false);

    // Refs for focus management
    const sidebarRef = useRef(null);
    const mainContentRef = useRef(null);
    const hamburgerRef = useRef(null);

    // Handle responsive behavior
    useEffect(() => {
        const handleResize = () => {
            const mobile = window.innerWidth <= 768;
            setIsMobile(mobile);

            // Remove collapsed state from sidebar on mobile and tablet
            if (mobile) {
                setIsSidebarCollapsed(false);
                setShowMobileOverlay(false);
            }
        };

        // Initial check
        handleResize();

        // Add event listener
        window.addEventListener('resize', handleResize);

        // Cleanup
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        if (!loggedIn) {
            // Redirect to login page if not logged in
            navigate('/tms/login');
        } else if (location.pathname === '/tms') {
            // Redirect logged-in users visiting /tms to /tms/home
            navigate('/tms/home');
        }
    }, [loggedIn, navigate, location.pathname]);

    // Toggle sidebar function to pass to Topbar
    const handleToggleSidebar = () => {
        if (isMobile) {
            // On mobile, toggle overlay instead of collapse state
            setShowMobileOverlay(prev => {
                const newState = !prev;
                // Focus management for mobile
                if (newState) {
                    // When opening, focus first navigation item
                    setTimeout(() => {
                        const firstNavItem = document.querySelector('.sidebar .nav-item-link');
                        if (firstNavItem) firstNavItem.focus();
                    }, 100);
                } else {
                    // When closing, return focus to hamburger menu
                    if (hamburgerRef.current) {
                        hamburgerRef.current.focus();
                    }
                }
                return newState;
            });
        } else {
            // On desktop/tablet, toggle collapse state
            setIsSidebarCollapsed(prev => {
                const newState = !prev;
                // Announce state change to screen readers
                const announcement = newState ? 'Sidebar collapsed' : 'Sidebar expanded';
                announceToScreenReader(announcement);
                return newState;
            });
        }
    };

    // Handle mobile overlay close
    const handleOverlayClose = useCallback(() => {
        setShowMobileOverlay(false);
        // Return focus to hamburger menu when closing overlay
        if (hamburgerRef.current) {
            hamburgerRef.current.focus();
        }
    }, []);

    // Screen reader announcement utility
    const announceToScreenReader = (message) => {
        const announcement = document.createElement('div');
        announcement.setAttribute('aria-live', 'polite');
        announcement.setAttribute('aria-atomic', 'true');
        announcement.setAttribute('class', 'sr-only');
        announcement.textContent = message;
        document.body.appendChild(announcement);
        setTimeout(() => {
            document.body.removeChild(announcement);
        }, 1000);
    };

    // Prevent body scroll when mobile sidebar is open
    useEffect(() => {
        if (isMobile && showMobileOverlay) {
            document.body.classList.add('mobile-sidebar-open');
        } else {
            document.body.classList.remove('mobile-sidebar-open');
        }

        // Cleanup on unmount
        return () => {
            document.body.classList.remove('mobile-sidebar-open');
        };
    }, [isMobile, showMobileOverlay]);

    // Handle keyboard navigation
    useEffect(() => {
        const handleKeyDown = (event) => {
            // Close mobile overlay on Escape key
            if (event.key === 'Escape' && isMobile && showMobileOverlay) {
                handleOverlayClose();
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isMobile, showMobileOverlay, handleOverlayClose]);

    return (
        <div className="app-container">
            {/* Skip to main content link for accessibility */}
            <a
                href="#main-content"
                className="skip-link"
                onClick={(e) => {
                    e.preventDefault();
                    if (mainContentRef.current) {
                        mainContentRef.current.focus();
                    }
                }}
            >
                Skip to main content
            </a>

            <Topbar
                onToggleSidebar={handleToggleSidebar}
                isSidebarCollapsed={isSidebarCollapsed}
                isMobile={isMobile}
                showMobileOverlay={showMobileOverlay}
                hamburgerRef={hamburgerRef}
            />
            <div className="layout-body">
                <Sidebar
                    ref={sidebarRef}
                    isCollapsed={isSidebarCollapsed}
                    isMobile={isMobile}
                    showMobileOverlay={showMobileOverlay}
                    onOverlayClose={handleOverlayClose}
                />
                <main
                    id="main-content"
                    ref={mainContentRef}
                    className={
                        `main-content ${isSidebarCollapsed ? '' : 'main-content-sidebar-open'} ${mainContentClass}`
                    }
                    role="main"
                    aria-label="Main content"
                    tabIndex={-1}
                >
                    {children}
                </main>
                {/* Mobile overlay backdrop */}
                {isMobile && showMobileOverlay && (
                    <div
                        className="mobile-overlay-backdrop"
                        onClick={handleOverlayClose}
                        onTouchStart={handleOverlayClose}
                    />
                )}
            </div>
        </div>
    );
};

export default Layout;
